import { useReducer } from "react";

import { IStoreExchangeFilters } from "src/@types/store-exchange";

import { DEFAULT_PER_PAGE, FIRST_PAGE } from "../types";

export type StoreExchangeTextField =
  | "controlNumber"
  | "customerDocument"
  | "operator";

export interface StoreExchangeFilterDraft {
  readonly storeCode: number | undefined;
  readonly startDate: string | undefined;
  readonly endDate: string | undefined;
  readonly status: string | undefined;
  readonly controlNumber: string;
  readonly customerDocument: string;
  readonly operator: string;
}

export interface StoreExchangeFilterState {
  readonly draft: StoreExchangeFilterDraft;
  readonly applied: IStoreExchangeFilters | undefined;
  readonly page: number;
  readonly perPage: number;
}

export type StoreExchangeFilterAction =
  | { type: "SET_STORE"; payload: number | undefined }
  | {
      type: "SET_PERIOD";
      payload: {
        startDate: string | undefined;
        endDate: string | undefined;
      };
    }
  | { type: "SET_STATUS"; payload: string | undefined }
  | {
      type: "SET_TEXT_FIELD";
      payload: { field: StoreExchangeTextField; value: string };
    }
  | { type: "APPLY_FILTERS"; payload: IStoreExchangeFilters }
  | { type: "SET_PAGE"; payload: number }
  | { type: "SET_PAGE_SIZE"; payload: number };

const INITIAL_DRAFT: StoreExchangeFilterDraft = {
  storeCode: undefined,
  startDate: undefined,
  endDate: undefined,
  status: undefined,
  controlNumber: "",
  customerDocument: "",
  operator: "",
};

export const INITIAL_STORE_EXCHANGE_FILTER_STATE: StoreExchangeFilterState = {
  draft: INITIAL_DRAFT,
  applied: undefined,
  page: FIRST_PAGE,
  perPage: DEFAULT_PER_PAGE,
};

export function storeExchangeFilterReducer(
  state: StoreExchangeFilterState,
  action: StoreExchangeFilterAction,
): StoreExchangeFilterState {
  if (action.type === "SET_STORE") {
    return { ...state, draft: { ...state.draft, storeCode: action.payload } };
  }

  if (action.type === "SET_PERIOD") {
    return {
      ...state,
      draft: {
        ...state.draft,
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      },
    };
  }

  if (action.type === "SET_STATUS") {
    return { ...state, draft: { ...state.draft, status: action.payload } };
  }

  if (action.type === "SET_TEXT_FIELD") {
    return {
      ...state,
      draft: { ...state.draft, [action.payload.field]: action.payload.value },
    };
  }

  if (action.type === "APPLY_FILTERS") {
    return { ...state, applied: action.payload, page: FIRST_PAGE };
  }

  if (action.type === "SET_PAGE") {
    return { ...state, page: action.payload };
  }

  if (action.type === "SET_PAGE_SIZE") {
    return { ...state, perPage: action.payload, page: FIRST_PAGE };
  }

  return state;
}

export interface StoreExchangeFilterMethods {
  readonly setStore: (storeCode: number | undefined) => void;
  readonly setPeriod: (
    startDate: string | undefined,
    endDate: string | undefined,
  ) => void;
  readonly setStatus: (value: string | undefined) => void;
  readonly setTextField: (
    field: StoreExchangeTextField,
    value: string,
  ) => void;
  readonly applyFilters: (filters: IStoreExchangeFilters) => void;
  readonly setPage: (page: number) => void;
  readonly setPageSize: (perPage: number) => void;
}

export interface UseStoreExchangeFilterReducer {
  readonly state: StoreExchangeFilterState;
  readonly methods: StoreExchangeFilterMethods;
}

export function useStoreExchangeFilterReducer(): UseStoreExchangeFilterReducer {
  const [state, dispatch] = useReducer(
    storeExchangeFilterReducer,
    INITIAL_STORE_EXCHANGE_FILTER_STATE,
  );

  const methods: StoreExchangeFilterMethods = {
    setStore: (storeCode: number | undefined): void =>
      dispatch({ type: "SET_STORE", payload: storeCode }),

    setPeriod: (
      startDate: string | undefined,
      endDate: string | undefined,
    ): void => dispatch({ type: "SET_PERIOD", payload: { startDate, endDate } }),

    setStatus: (value: string | undefined): void =>
      dispatch({ type: "SET_STATUS", payload: value }),

    setTextField: (field: StoreExchangeTextField, value: string): void =>
      dispatch({ type: "SET_TEXT_FIELD", payload: { field, value } }),

    applyFilters: (filters: IStoreExchangeFilters): void =>
      dispatch({ type: "APPLY_FILTERS", payload: filters }),

    setPage: (page: number): void =>
      dispatch({ type: "SET_PAGE", payload: page }),

    setPageSize: (perPage: number): void =>
      dispatch({ type: "SET_PAGE_SIZE", payload: perPage }),
  };

  return { state, methods };
}
